import React from 'react';
import { Box, Drawer, IconButton, Link, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { QuestionListRow } from './QuestionListRow';

/**
 * The phone stand-in for the question rail: a sheet that rises from the bottom
 * of the screen, covering roughly two thirds of it and scrolling on its own.
 *
 * Every page that shows one question at a time on a phone navigates through
 * this, so a row carries the question's text as well as its number — on a screen
 * showing a single question, a column of bare numbers says nothing about where
 * it would take you.
 *
 * Props:
 *   id          (string)   - ID prefix for generated element IDs.
 *   isOpen      (bool)     - Whether the sheet is showing.
 *   onClose     (function) - Dismiss the sheet.
 *   questions   (array)    - The question text, one entry per question.
 *   results     (array)    - Output of gradeQuiz, one entry per question. Left
 *                            out by the editor, which has no answers to mark.
 *   isGraded    (bool)     - Whether the quiz has been submitted.
 *   activeIndex (number)   - The question currently on screen.
 *   onSelect    (function) - Called with the index to jump to.
 *   onBack      (function) - Optional; leaves the questions for the page that
 *                            lists them, which is the results page's score.
 */
export const QuestionListSheet = ({
  id,
  isOpen,
  onClose,
  questions,
  results,
  isGraded,
  activeIndex,
  onSelect,
  onBack,
}) => (
  <Drawer
    anchor="bottom"
    open={isOpen}
    onClose={onClose}
    className="mss__questionListSheet"
    id={`${id}__questionListSheet`}
  >
    <Box className="sheetHeader">
      <Typography className="sheetTitle" component="h2">
        Question List
      </Typography>
      <IconButton
        id={`${id}__button_closeQuestionList`}
        className="sheetClose"
        onClick={onClose}
        aria-label="Close question list"
      >
        <FontAwesomeIcon icon={faXmark} />
      </IconButton>
    </Box>

    {/*
      The way back out of a single question: on a phone the results page is a
      list of its own, and this sheet is where the learner already is when they
      want to leave the question they opened from it.
    */}
    {onBack && (
      <Link
        component="button"
        className="sheetBackLink"
        id={`${id}__link_backToResults`}
        onClick={() => {
          onBack();
          onClose();
        }}
      >
        Quiz Results
      </Link>
    )}

    <Box className="sheetList" component="nav" aria-label="Quiz questions">
      {questions.map((text, index) => (
        <QuestionListRow
          key={index}
          id={`${id}__sheetItem_${index + 1}`}
          number={index + 1}
          text={text}
          result={results?.[index]}
          isGraded={isGraded}
          isActive={index === activeIndex}
          onSelect={() => {
            onSelect(index);
            onClose();
          }}
        />
      ))}
    </Box>
  </Drawer>
);
